import { isNil } from 'utils'
import { z } from 'zod'

import { PlatformWidePermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { oneItem } from '../shared'
import { accountQueryKey } from './shared'

export const userAccountSchema = z.object({
  username: z.string(),
  email: z.string(),
  displayName: z.string(),
  acceptedEulaDate: z.coerce.date().optional(),
  admin: z.boolean().optional().default(false),
  support: z.boolean().optional().default(false),
  marketingDisabled: z.boolean().default(false)
})

const userAccountResponseSchema = oneItem(userAccountSchema)

export const userAccountApi = defineApi({
  pathSchema: z.object({
    username: z.string()
  }),
  pathFor: (p) => `/useraccounts/${p.username}`,
  name: 'GetUserAccount',
  description: 'Retrieve user account details including, name, email, and EULA acceptance status',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  responseSchema: userAccountResponseSchema,
  gatewayPath: '/useraccounts/:username' as const
})

export const updateMarketingApi = defineApi({
  pathSchema: z.object({
    username: z.string(),
    op: z.enum(['enable', 'disable'])
  }),
  pathFor: (p) => `/useraccounts/${p.username}/features/marketing/${p.op}`,
  name: 'UpdateMarketingPreference',
  description: 'Enable or disable marketing email communications for a user',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'PUT',
  responseSchema: z.void(),
  gatewayPath: '/useraccounts/:username/features/marketing/:op' as const,
  invalidateQueries: () => [accountQueryKey]
})

// Updating this date will trigger the EULA acceptance flow for all users who
// have not accepted the EULA since this date.
export const eulaEffectiveDate = new Date('2025-09-12T00:00:00Z')

export enum EulaStatus {
  /** User has never accepted any EULA */
  NeverAccepted = 'never_accepted',
  /** User accepted an old EULA but needs to accept the updated one */
  NeedsUpdate = 'needs_update',
  /** User has accepted the current EULA */
  UpToDate = 'up_to_date'
}

export function getEulaStatus(userAcceptedDate: Date | undefined): EulaStatus {
  if (isNil(userAcceptedDate)) {
    return EulaStatus.NeverAccepted
  }

  if (userAcceptedDate < eulaEffectiveDate) {
    return EulaStatus.NeedsUpdate
  }

  return EulaStatus.UpToDate
}
